import type { SyncManager } from '@wiztime/sync';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toastInfo } from '../../shell/toastStore';
import { Button, ButtonVariant } from '../../ui/Button';
import {
  AccountFailure,
  AccountFormFrame,
  EmailField,
  isValidEmail,
  PasswordField,
  useAccountSubmit,
} from './accountForm';
import styles from './Sync.module.css';
import { toConfigUrl } from './urls';

interface LoginFlowProps {
  readonly manager: SyncManager;
  readonly serverUrl: string;
  /** Prefilled after a password reset. */
  readonly initialEmail?: string;
  /** Shown above the form, e.g. "Password reset". */
  readonly notice?: string;
  readonly onBack: () => void;
  readonly onUnsupported: () => void;
  /** Logged in (sync is on); the first sync of the account's data has just started. */
  readonly onActivated?: () => void;
}

/** Log in to an existing account on this device. */
export function LoginFlow({
  manager,
  serverUrl,
  initialEmail = '',
  notice,
  onBack,
  onUnsupported,
  onActivated,
}: LoginFlowProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const submit = useAccountSubmit(onUnsupported);
  const valid = isValidEmail(email) && password !== '';

  const login = async (): Promise<void> => {
    const done = await submit.run(async () => {
      await manager.login({ serverUrl: toConfigUrl(serverUrl), email: email.trim(), password });
      return true;
    });
    if (done === true) {
      onActivated?.();
      toastInfo(t('sync.account.loginFlow.done'));
    }
  };

  return (
    <AccountFormFrame
      title={t('sync.account.loginFlow.title')}
      busy={submit.busy}
      testId="account-login"
      onSubmit={() => valid && void login()}
      status={
        <>
          {notice === undefined ? null : (
            <p className={styles.good} role="status" data-testid="account-notice">
              {notice}
            </p>
          )}
          <AccountFailure
            failure={submit.failure}
            retryable={submit.retryable}
            onRetry={() => void login()}
          />
        </>
      }
      actions={
        <>
          <Button disabled={submit.busy} onClick={onBack} data-testid="account-back">
            {t('sync.back')}
          </Button>
          <Button
            type="submit"
            variant={ButtonVariant.Primary}
            disabled={!valid || submit.busy}
            data-testid="account-submit"
          >
            {t('sync.account.loginFlow.submit')}
          </Button>
        </>
      }
    >
      <EmailField
        label={t('sync.account.field.email')}
        value={email}
        onChange={setEmail}
        testId="account-email"
        invalid={email !== '' && !isValidEmail(email)}
        message={email !== '' && !isValidEmail(email) ? t('sync.account.invalid.email') : undefined}
      />
      <PasswordField
        label={t('sync.account.field.password')}
        value={password}
        onChange={setPassword}
        testId="account-password"
      />
    </AccountFormFrame>
  );
}
